/**
 * Conversation List Component
 * Displays list of conversations with shadow metadata and infinite scroll
 */

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useRef } from "react";
import type { ConversationSummary } from "@/services/message.service";
import { LoaderCircle } from "lucide-react";

interface ConversationListProps {
  conversations: ConversationSummary[];
  selectedConversationId?: number;
  onSelectConversation: (conversationId: number) => void;
  isLoading?: boolean;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

const formatTimeAgo = (date: Date): string => {
  return formatDistanceToNow(date, { addSuffix: true })
    .replace(/^about /, "")
    .replace(/ seconds?/, "s")
    .replace(/ minutes?/, "m")
    .replace(/ hours?/, "h")
    .replace(/ days?/, "d")
    .replace(/ months?/, "mo")
    .replace(/ years?/, "y");
};

export const ConversationList = ({
  conversations,
  selectedConversationId,
  onSelectConversation,
  isLoading,
  hasMore,
  isFetchingNextPage,
  onLoadMore,
}: ConversationListProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasMore &&
          !isFetchingNextPage &&
          onLoadMore
        ) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isFetchingNextPage, onLoadMore]);

  if (isLoading && conversations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-sm text-muted-foreground">
          Loading conversations...
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-sm text-muted-foreground text-center px-4">
          No conversations found
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full min-w-0 overflow-hidden">
      <style>{`
        .conversation-scroll-area [data-slot="scroll-area-viewport"] {
          min-width: 0 !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
          display: block !important;
        }
        .conversation-scroll-area [data-slot="scroll-area-viewport"] > div {
          min-width: 0 !important;
          max-width: 100% !important;
          display: block !important;
        }
        .conversation-scroll-area [data-slot="scroll-area-scrollbar"] {
          top: 115px !important;
        }
      `}</style>
      <ScrollArea className="conversation-scroll-area h-full w-full min-w-0 [&>div]:min-w-0! [&>div]:max-w-full!" ref={scrollAreaRef}>
        <div className="space-y-1 px-2 pt-[110px] w-full min-w-0 max-w-full overflow-hidden box-border">
          {conversations.map((conversation) => {
            const isSelected =
              conversation.conversationId === selectedConversationId;
            const lastMessageDate = conversation.lastMessageAt
              ? new Date(conversation.lastMessageAt)
              : null;
            const timeAgo = lastMessageDate
              ? formatTimeAgo(lastMessageDate)
              : "No activity";

            const contactName = conversation.contact?.name || "Unknown Contact";
            const lastMessagePreview = conversation.lastMessage?.content || "No messages";

            const category = conversation.categories
              ? conversation.categories[0]
              : null;

            return (
              <div key={conversation.conversationId} className="min-w-0 overflow-hidden">
                <button
                  onClick={() =>
                    onSelectConversation(conversation.conversationId)
                  }
                  className={cn(
                    "block w-full h-[100px] min-w-0 overflow-hidden text-left p-3 rounded-2xl transition-colors cursor-pointer",
                    "hover:bg-gray-100",
                    isSelected && "bg-gray-100 rounded-2xl",
                  )}
                >
                  <div className="flex flex-col items-start justify-between w-full min-w-0">
                    <div className="flex flex-col w-full min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="truncate text-[13px] font-medium">
                          {contactName}
                        </div>
                        <div className="text-[0.625rem] text-muted-foreground shrink-0">
                          {timeAgo}
                        </div>
                      </div>

                      <div className="flex flex-col mb-1">
                      {conversation.conversationId && (
                          <div className="text-[0.625rem] text-gray-700 w-full truncate mb-0.5">
                            # {conversation.conversationId}
                          </div>
                        )}

                        <div className={cn(
                          "text-[0.625rem] text-black-50 w-full",
                          category ? "truncate" : "line-clamp-2 mb-1.5"
                        )}>
                          {lastMessagePreview}
                        </div>
                      </div>

                      {category && (
                        <div className="flex items-center gap-2 bg-white text-accent-purple border border-gray-400 rounded-sm w-fit py-1 px-3 text-[0.625rem] h-[22px]">
                          <span className="h-1.5 w-1.5 bg-accent-purple rounded-[1px]" />
                          {category}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
                <div className="mx-1 border-b border-gray-200 mt-1" />
              </div>
            );
          })}
          {/* Infinite scroll trigger */}
          {hasMore && (
            <div ref={observerTarget} className="py-2 text-center">
              {isFetchingNextPage ? (
                <div className="text-xs text-muted-foreground">
                  <LoaderCircle
                    size={18}
                    className="animate-spin mx-auto w-full"
                  />
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Scroll for more
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
